/**
 * formatMicros is the one formatter for an amount the client works out itself
 * (00-work-providers-clients §Web client).
 *
 * Every other figure comes formatted from the server. Two cannot: the running
 * total of calls a picture sheet has made, which exists only in that sheet,
 * and the price of a batch of portraits, which depends on how many the author
 * has ticked (ADR-0049). The same two-decimal shape as the server's, so they
 * read as the same kind of number as every price beside them.
 */
export function formatMicros(micros: number): string {
  if (micros > 0 && micros < 10_000) return "<$0.01";
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

/**
 * aroundAmount splits a translated sentence at the amount it takes, so the
 * amount can be isolated left to right on its own and the words around it stay
 * in the interface's direction (05 §Web client: an amount inside a label). A
 * sentence keeps its own word order: "$0.07 each", «هر کدام $0.07».
 */
export function aroundAmount(sentence: (amount: string) => string): [string, string] {
  const mark = "\u0000";
  const [before = "", after = ""] = sentence(mark).split(mark);
  return [before, after];
}

/**
 * MICROS is USD × 10⁻⁶, the unit every amount crosses the wire in (rule 7).
 */
const MICROS = 1_000_000;

/**
 * parseAmount turns an amount somebody typed into micros, or null if it is not
 * an amount.
 *
 * It lives beside formatMicros because parsing and formatting that disagree
 * about cents is a limit nobody set (ADR-0082). Two rules it must keep:
 *
 *   - **Round, never truncate.** `12.34 * 1e6` is 12339999.999999998 in
 *     IEEE754, and truncating stores a limit one micro below the one chosen.
 *   - **Persian digits are digits.** A phone with a Persian keyboard types
 *     ۱۲٫۵, and an ASCII-only parser reads that as nothing — or, worse, as 12.
 *     Arabic-Indic digits, the Arabic decimal separator and its thousands
 *     separator are normalised before anything else looks at the string.
 */
export function parseAmount(typed: string): number | null {
  const normalised = typed
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\u066B/g, ".") // the Arabic decimal separator
    // Then what a person types around a figure: the Arabic thousands
    // separator, ZWNJ, a comma, spaces, a dollar sign. Written as escapes
    // because two of them are invisible in a source file.
    .replace(/[\u066C\u200C,\s$]/g, "");
  if (!/^\d+(\.\d+)?$/.test(normalised)) return null;
  return Math.round(Number(normalised) * MICROS);
}
